import {
  MapPinProps,
  MapPinTypeVariant,
  MapPinVariant,
} from '@user/components/HereMap/MapPin/types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { APIError, get, getNoRedirect, post } from '../services/crud'
import type { LocationDTO } from '@sales-center/common'
import { ENTITY_TYPES, ServiceConfig } from '@sales-center/common'
import { ILeadDetail } from '@user/store/lead'
import { IOpportunityDetail } from '@user/store/opportunity'
import {
  clearFallbackLocation,
  setEntityDetails,
  setFallbackLocation,
  setFallbackLocationStatus,
  setSelectedEntityStatus,
  setSelectedEntityVariant,
  setSelectedServiceLocationsDetails,
  setSelectedServiceLocationsDetailsStatus,
} from '.'
import {
  EntityDetail,
  EntityLocationDTO,
  MultiTenantLocation,
  RecordsAtLocationResponse,
  MapPinDTO,
  SearchAreaResponse,
  isPestPacMember,
} from './typings'
import {
  areColocatedCoordinates,
  cellKey,
  neighbouringCellKeys,
} from './colocation'
import {
  KNOCK_STATUS_FIELDS,
  pestPacMemberAsSinglePin,
  pestPacPinAsMember,
} from './pestPacPin'

import { ILeadOppProduct } from '../leadOppProducts'
import { DraftLead } from '../draftLeads/typings'
import { selectAllKnockStatuses } from '../knockStatus/slice'
import { StoreData } from '../reducers/typings'

function normalizeEntityDetail(
  variant: MapPinProps['variant'],
  raw: ILeadDetail | IOpportunityDetail | DraftLead,
): EntityDetail {
  return {
    ...(raw as any),
    entityType: variantToEntityType(variant),
  } as EntityDetail
}

export const getEntityDetail = createAsyncThunk<
  EntityDetail,
  [
    variant: MapPinProps['variant'],
    entityId: number,
    serviceLocationId: number,
    noLoading?: boolean,
  ],
  { rejectValue: APIError }
>(
  'map/getEntityDetail',
  async (
    [variant, entityId, serviceLocationId, noLoading = false],
    thunkApi,
  ) => {
    if (!noLoading) {
      thunkApi.dispatch(setSelectedEntityStatus('loading'))
      thunkApi.dispatch(setSelectedEntityVariant(variant))
      thunkApi.dispatch(setEntityDetails(null))
      thunkApi.dispatch(clearFallbackLocation())
    }

    let url = ''
    switch (variant) {
      case 'lead':
        url = `${ServiceConfig.baseAPIUrl}/Lead/${entityId}`
        break
      case 'opportunity':
        url = `${ServiceConfig.baseAPIUrl}/Opportunity/${entityId}`
        break
      case 'pendinglead':
      case 'draftlead':
        url = `${ServiceConfig.baseAPIUrl}/DraftLeads/details/${entityId}`
        break
      default:
        thunkApi.dispatch(setSelectedEntityStatus('error'))
        return thunkApi.rejectWithValue({
          Message: 'Unsupported variant type',
          StatusCode: 400,
        } as APIError)
    }

    const response = await getNoRedirect(url)

    if (response.ok) {
      const raw: ILeadDetail | IOpportunityDetail | DraftLead =
        await response.json()
      const entity = normalizeEntityDetail(variant, raw)
      return entity
    } else {
      thunkApi.dispatch(setSelectedEntityStatus('error'))
      // Attempt fallback to fetch only the LocationDTO using the serviceLocationId
      if (
        variant === 'pendinglead' ||
        variant === 'draftlead' ||
        serviceLocationId > 0
      ) {
        if (!noLoading) {
          thunkApi.dispatch(setFallbackLocationStatus('loading'))
        }
        let fbUrl = ''
        switch (variant) {
          case 'lead':
            fbUrl = `${ServiceConfig.baseAPIUrl}/leadlocation/basicInfo/${serviceLocationId}`
            break
          case 'opportunity':
            fbUrl = `${ServiceConfig.baseAPIUrl}/opportunitylocation/basicInfo/${serviceLocationId}`
            break
          case 'pendinglead':
          case 'draftlead':
            fbUrl = `${ServiceConfig.baseAPIUrl}/draftleads/basicInfo/${entityId}`
            break
          default:
            fbUrl = ''
            break
        }
        if (fbUrl) {
          try {
            const fbResponse = await get(fbUrl)
            if (fbResponse.ok) {
              const loc: LocationDTO = await fbResponse.json()
              thunkApi.dispatch(setFallbackLocation(loc))
              if (!noLoading) {
                thunkApi.dispatch(setFallbackLocationStatus('success'))
              }
            } else {
              if (!noLoading) {
                thunkApi.dispatch(setFallbackLocationStatus('error'))
              }
            }
          } catch {
            if (!noLoading) {
              thunkApi.dispatch(setFallbackLocationStatus('error'))
            }
          }
        }
      }
    }

    try {
      const err = (await response.json()) as APIError
      return thunkApi.rejectWithValue(err)
    } catch {
      return thunkApi.rejectWithValue({
        Message: 'Failed to load entity detail',
        StatusCode: response.status,
      } as APIError)
    }
  },
)

export const getEntityProducts = createAsyncThunk<
  ILeadOppProduct[],
  [variant: MapPinProps['variant'], entityId: number],
  { rejectValue: APIError }
>('map/getEntityProducts', async ([variant, entityId], thunkApi) => {
  let url = ''
  switch (variant) {
    case 'lead':
      url = `${ServiceConfig.baseAPIUrl}/LeadProduct/lead/${entityId}`
      break
    case 'opportunity':
      url = `${ServiceConfig.baseAPIUrl}/OpportunityProduct/opportunity/${entityId}`
      break
    default:
      return thunkApi.rejectWithValue({
        Message: 'Unsupported variant type',
        StatusCode: 400,
      } as APIError)
  }

  const response = await get(url)

  if (response.ok) {
    const products = await response.json()
    return products
  }

  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export interface AreaParams {
  long1: number
  lat1: number
  long2: number
  lat2: number
  zoom: number
  foundLocationFromSearch: boolean
  entityTypes: string
  ownerId: string | null
  overrideFilters?: boolean
  forceUpdate?: boolean
  knockStatusIds?: string
  includeRecordsWithoutKnockStatus?: boolean
  knockStatusLocationsOnly?: boolean
  pestPacActive?: boolean
  signal?: AbortSignal
}

function adjustTotals(
  payload: SearchAreaResponse,
  locs: EntityLocationDTO[],
  ownerId: string | null,
  delta: 1 | -1,
) {
  for (const loc of locs) {
    const n = loc.count * delta
    const type = loc.entityType.toLowerCase()
    payload.total += n
    if (type === 'lead') {
      payload.totalLead += n
      if (ownerId) payload.myLead += n
    } else if (type === 'opportunity') {
      payload.totalOpportunity += n
      if (ownerId) payload.myOpportunity += n
    } else if (type === 'draftlead' || type === 'pendinglead') {
      payload.totalDraftLead += n
      if (ownerId) payload.myDraftLead += n
    }
    if (ownerId) payload.myRecords += n
  }
}

export const getContactLocationsInArea = createAsyncThunk<
  SearchAreaResponse,
  AreaParams,
  { state: StoreData; rejectValue: APIError }
>('map/getContactLocationsInArea', async (params, thunkApi) => {
  const {
    long1,
    lat1,
    long2,
    lat2,
    zoom,
    entityTypes,
    ownerId,
    overrideFilters,
    knockStatusIds,
    includeRecordsWithoutKnockStatus,
    knockStatusLocationsOnly,
  } = params
  // When overrideFilters is true, use all entity types regardless of current filter settings
  const effectiveEntityTypes = overrideFilters
    ? 'draftlead_lead_opportunity'
    : entityTypes

  // SH-2100: same override for knock status — otherwise the backend excludes a hidden-KS
  // location from the response before the client ever sees it (parallel to effectiveEntityTypes).
  const effectiveKnockStatusIds = overrideFilters
    ? selectAllKnockStatuses(thunkApi.getState())
        .map((ks) => ks.id)
        .join('_')
    : knockStatusIds

  const baseUrl = `${ServiceConfig.baseAPIUrl}/ContactLocations/SearchArea?long1=${long1}&lat1=${lat1}&long2=${long2}&lat2=${lat2}&zoom=${zoom}&entityTypes=${effectiveEntityTypes}${ownerId ? `&ownerId=${ownerId}` : ''}`
  const knockStatusParam =
    effectiveKnockStatusIds && effectiveKnockStatusIds.trim() !== ''
      ? `&knockStatusIds=${effectiveKnockStatusIds}`
      : ''
  const url = `${baseUrl}${knockStatusParam}`

  const response = await get(
    url,
    {},
    params.signal ? { signal: params.signal } : {},
  )

  if (params.signal?.aborted) {
    return thunkApi.rejectWithValue({
      Message: 'Request aborted',
      StatusCode: 0,
    } as APIError)
  }

  if (response.ok) {
    const payload: SearchAreaResponse = await response.json()
    payload.filters = `ownerId=${ownerId || 'null'}&entityTypes=${effectiveEntityTypes}&knockStatusIds=${knockStatusIds || ''}`

    if (!knockStatusIds || knockStatusIds.trim() === '') {
      // Remove standalone KS-only pins (entityId === 0) when no KS filter is active.
      // Entity pins (leads/opportunities) with KS color are kept so their badge renders.
      // PR review: gate on the real filter (knockStatusIds), not effectiveKnockStatusIds —
      // the latter is forced to every tenant knock status whenever overrideFilters is true
      // (see above), which made this declutter never fire during an address search with no
      // KS filter selected, leaking every standalone KS-only pin onto the map.
      const removed = payload.locations.filter(
        (loc) => loc.entityId === 0 && !!loc.knockStatusSettingColor,
      )
      payload.locations = payload.locations.filter(
        (loc) => !(loc.entityId === 0 && !!loc.knockStatusSettingColor),
      )
      adjustTotals(payload, removed, ownerId, -1)
    }

    // Only hard-strip entity pins here for the normal (non-search) view — during an address
    // search (overrideFilters), stripping unconditionally here discards the data before the
    // reducer's search-match resolution ever runs, so a searched-for hidden lead/opportunity/
    // pending-lead could never be preserved and the search silently returned nothing. Search
    // mode instead re-applies this filter client-side via filterEntityPinsForKnockStatusLocationsOnly,
    // the same preserve-match pattern already used for the entityTypes and knock-status filters.
    if (knockStatusLocationsOnly && !overrideFilters) {
      const entityPins = payload.locations.filter((loc) => loc.entityId > 0)
      payload.locations = payload.locations.filter((loc) => loc.entityId === 0)
      adjustTotals(payload, entityPins, ownerId, -1)
    }

    if (
      (overrideFilters || includeRecordsWithoutKnockStatus) &&
      !knockStatusLocationsOnly &&
      effectiveKnockStatusIds &&
      effectiveKnockStatusIds.trim() !== ''
    ) {
      const secondaryResponse = await get(
        baseUrl,
        {},
        params.signal ? { signal: params.signal } : {},
      )
      if (!params.signal?.aborted && secondaryResponse.ok) {
        const secondaryPayload: SearchAreaResponse =
          await secondaryResponse.json()
        const existingPinIds = new Set(
          payload.locations.map((loc) => loc.pinId),
        )
        const locationsWithoutKnockStatus = secondaryPayload.locations.filter(
          (loc) =>
            loc.entityId > 0 &&
            !loc.knockStatusSettingColor &&
            !existingPinIds.has(loc.pinId),
        )
        payload.locations = [
          ...payload.locations,
          ...locationsWithoutKnockStatus,
        ]
        adjustTotals(payload, locationsWithoutKnockStatus, ownerId, 1)
      }
    }

    // SH-2100: resolve the real (unmodified) knock-status filter selection to setting names so
    // the reducer can re-apply it to what actually renders, the same way it already does for the
    // real entityTypes selection (meta.arg.entityTypes / filterLocationsByEntityTypes).
    if (overrideFilters && knockStatusIds && knockStatusIds.trim() !== '') {
      const realKnockStatusIds = new Set(
        knockStatusIds.split('_').filter(Boolean),
      )
      const resolvedNames = selectAllKnockStatuses(thunkApi.getState())
        .filter((ks) => realKnockStatusIds.has(String(ks.id)))
        .map((ks) => ks.name)
      // PR review: only attach when at least one id actually resolved — an empty
      // array is still truthy, so an unconditional attach would make every
      // downstream consumer treat "nothing resolved" as "hide everything".
      if (resolvedNames.length > 0) {
        payload.filteredKnockStatusNames = resolvedNames
      }
    }

    return payload
  }

  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// SH-1143 — Single "what's at this coordinate" lookup, UNFILTERED by the user's active map filters.
// Returns every record at the coordinate (to detect records hidden by the current filter selections)
// plus the active knock-status info (for the add-lead / knock-status preview flow). Consolidates the
// former GET /KnockStatusLocations/byCoordinates.
export const getRecordsAtLocation = createAsyncThunk<
  RecordsAtLocationResponse,
  { lat: number; lng: number },
  { rejectValue: APIError }
>('map/getRecordsAtLocation', async ({ lat, lng }, thunkApi) => {
  const response = await get(
    `${ServiceConfig.baseAPIUrl}/ContactLocations/recordsAtLocation?lat=${lat}&lng=${lng}`,
  )
  if (response.ok) return (await response.json()) as RecordsAtLocationResponse
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

const supportedVariants: MapPinVariant[] = [
  'lead',
  'opportunity',
  'pendinglead',
  'draftlead',
]

function isSupportedVariant(variant: string): variant is MapPinVariant {
  return supportedVariants.includes(variant as MapPinVariant)
}

function variantToEntityType(variant: MapPinProps['variant']): ENTITY_TYPES {
  switch (variant) {
    case 'lead':
      return ENTITY_TYPES.Lead
    case 'opportunity':
      return ENTITY_TYPES.Opportunity
    case 'pendinglead':
    case 'draftlead':
      return ENTITY_TYPES.DraftLead
    default:
      throw new Error(`Unsupported variant: ${variant}`)
  }
}

export const fetchEntityDetailAndProducts = createAsyncThunk<
  EntityDetail | null,
  [
    variant: MapPinProps['variant'],
    entityId: number,
    serviceLocationId: number,
    noLoading?: boolean,
  ],
  { rejectValue: APIError }
>(
  'map/fetchEntityDetailAndProducts',
  async (
    [variant, entityId, serviceLocationId, noLoading = false],
    thunkApi,
  ) => {
    if (!isSupportedVariant(variant)) {
      return thunkApi.rejectWithValue({
        Message: 'Unsupported variant type',
        StatusCode: 400,
      } as APIError)
    }

    const detailResult = await thunkApi.dispatch(
      getEntityDetail([variant, entityId, serviceLocationId, noLoading]),
    )

    if (!getEntityDetail.fulfilled.match(detailResult)) {
      return null
    }

    if (variant !== 'pendinglead' && variant !== 'draftlead') {
      const productsResult = await thunkApi.dispatch(
        getEntityProducts([variant, entityId]),
      )

      if (getEntityProducts.fulfilled.match(productsResult)) {
        return {
          ...detailResult.payload,
          services: productsResult.payload,
        }
      }
    }

    return {
      ...detailResult.payload,
      services: [],
    }
  },
)

export const fetchMultiTenantEntityDetail = createAsyncThunk<
  LocationDTO[],
  number[],
  { rejectValue: APIError }
>('map/fetchMultiTenantEntityDetail', async (serviceLocationIds, thunkApi) => {
  const url = `${ServiceConfig.baseAPIUrl}/ContactLocations/serviceLocationDetails`
  thunkApi.dispatch(setSelectedServiceLocationsDetailsStatus('loading'))
  thunkApi.dispatch(setSelectedServiceLocationsDetails([]))

  if (serviceLocationIds.length === 0) return []

  const response = await post(url, serviceLocationIds)

  if (response.ok) {
    return await response.json()
  }

  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export interface MapConfigDTO {
  maxZoom: number
  minZoomForAddLead: number
  pestPacMinZoom: number
}

// Cached at module scope so every caller (useMapLibreInstance, useApi, etc.)
// shares a single request instead of each independently re-fetching the same
// static config. Cleared on failure/null so a later call can retry.
let cachedMapConfigPromise: Promise<MapConfigDTO | null> | null = null

export function fetchMapConfig(): Promise<MapConfigDTO | null> {
  if (!cachedMapConfigPromise) {
    cachedMapConfigPromise = (async () => {
      try {
        const resp = await get(`${ServiceConfig.baseAPIUrl}/MapConfig`)
        if (!resp.ok) return null
        return (await resp.json()) as MapConfigDTO
      } catch {
        return null
      }
    })()

    cachedMapConfigPromise.then((config) => {
      if (!config) cachedMapConfigPromise = null
    })
  }

  return cachedMapConfigPromise
}

export interface MapBoundingBox {
  long1: number
  lat1: number
  long2: number
  lat2: number
}

export interface PestPacFetchResult {
  // false for a non-OK HTTP response or an unparseable body — distinct from a
  // legitimate empty `locations` result, which callers must treat differently
  // (e.g. still clearing stale pins vs. surfacing a non-blocking error).
  ok: boolean
  locations: MapPinDTO[]
}

// PestPac locations are fetched separately from Sales Center locations — the
// backend SearchArea endpoint returns one source or the other per call, never both.
export async function fetchPestPacLocations(
  bbox: MapBoundingBox,
  zoom: number,
  knockStatusIds?: string,
  includeWithoutKnockStatus?: boolean,
): Promise<PestPacFetchResult> {
  const ksParam = knockStatusIds
    ? `&knockStatusIds=${encodeURIComponent(knockStatusIds)}`
    : ''
  const includeWithoutKsParam = includeWithoutKnockStatus
    ? '&includeWithoutKnockStatus=true'
    : ''
  const url =
    `${ServiceConfig.baseAPIUrl}/ContactLocations/SearchArea` +
    `?long1=${bbox.long1}&lat1=${bbox.lat1}&long2=${bbox.long2}&lat2=${bbox.lat2}` +
    `&zoom=${zoom}&source=PestPac` +
    ksParam +
    includeWithoutKsParam

  const response = await get(url)
  if (!response.ok) return { ok: false, locations: [] }
  try {
    const data = await response.json()
    return { ok: true, locations: (data.locations as MapPinDTO[]) ?? [] }
  } catch {
    return { ok: false, locations: [] }
  }
}

// Sales Center pins take precedence over PestPac pins for the same account:
// suppress any PestPac pin whose PestPac location ID matches a linked
// pestPacLocationId already present on an open (not closed) Sales Center pin
// in this result set. SH-1312: closed SC records no longer suppress their
// linked PestPac pin — see excludeClosedLinkedSalesCenterPins below.
// Matching requires BOTH the same PestPac account AND co-located coordinates —
// an account can have pins at more than one physical spot over time (e.g. a
// leftover knock-status tie at an old address after the account's address
// changed), and matching on account id alone would wrongly suppress a pin at
// a genuinely different location just because it shares the same account.
export function excludeLinkedPestPacPins(
  salesCenterLocations: MapPinDTO[],
  pestPacLocations: MapPinDTO[],
): MapPinDTO[] {
  if (pestPacLocations.length === 0) return pestPacLocations

  const linkedCoordsByAccount = new Map<
    string,
    { lat?: number | null; lng?: number | null }[]
  >()
  const indexLink = (
    pestPacLocationId: string | null | undefined,
    lat?: number | null,
    lng?: number | null,
  ) => {
    if (!pestPacLocationId) return
    const coords = linkedCoordsByAccount.get(pestPacLocationId)
    if (coords) coords.push({ lat, lng })
    else linkedCoordsByAccount.set(pestPacLocationId, [{ lat, lng }])
  }

  for (const loc of salesCenterLocations) {
    if (loc.isClosed) continue
    indexLink(loc.pestPacLocationId, loc.latitude, loc.longitude)
    for (const member of loc.entities ?? []) {
      indexLink(
        member.pestPacLocationId,
        member.latitude ?? loc.latitude,
        member.longitude ?? loc.longitude,
      )
    }
  }
  if (linkedCoordsByAccount.size === 0) return pestPacLocations

  const isLinked = (
    pestPacLocationId: string | null | undefined,
    lat?: number | null,
    lng?: number | null,
  ) => {
    if (!pestPacLocationId) return false
    const coords = linkedCoordsByAccount.get(pestPacLocationId)
    if (!coords) return false

    return coords.some((coord) =>
      areColocatedCoordinates(coord.lat, coord.lng, lat, lng),
    )
  }

  return pestPacLocations.flatMap((pin) => {
    if (!pin.multitenant) {
      return isLinked(
        pin.pestPacLocationId ?? pin.pestPacDetails?.locationId,
        pin.latitude,
        pin.longitude,
      )
        ? []
        : [pin]
    }

    const remaining = (pin.entities ?? []).filter(
      (member) =>
        !isLinked(
          member.pestPacLocationId,
          member.latitude ?? pin.latitude,
          member.longitude ?? pin.longitude,
        ),
    )

    if (remaining.length === (pin.entities ?? []).length) return [pin]
    if (remaining.length === 0) return []
    if (remaining.length === 1 && isPestPacMember(remaining[0]))
      return [pestPacMemberAsSinglePin(remaining[0], pin)]

    return [{ ...pin, count: remaining.length, entities: remaining }]
  })
}

// SH-1312: Given a closed Sales Center record (Closed/Won, Closed/Lost, or
// Disqualified) that's linked to a PestPac account, PestPac becomes the
// source of truth — suppress the SC pin so only the linked PestPac pin shows.
// Only suppresses the SC pin when its linked PestPac pin is actually present
// in this result set; otherwise there'd be nothing left to represent the location.
export function excludeClosedLinkedSalesCenterPins(
  salesCenterLocations: MapPinDTO[],
  pestPacLocations: MapPinDTO[],
): MapPinDTO[] {
  if (pestPacLocations.length === 0) return salesCenterLocations

  const availablePestPacLocationIds = new Set(
    pestPacLocations
      .flatMap((loc) => [
        loc.pestPacDetails?.locationId,
        ...(loc.entities ?? []).map((member) => member.pestPacLocationId),
      ])
      .filter((id): id is string => !!id),
  )
  if (availablePestPacLocationIds.size === 0) return salesCenterLocations

  return salesCenterLocations.filter(
    (loc) =>
      !(
        loc.isClosed &&
        loc.pestPacLocationId &&
        availablePestPacLocationIds.has(loc.pestPacLocationId)
      ),
  )
}

// SH-1341: getContactLocationsInArea forces the backend to return every
// Sales Center entity type when overrideFilters is set (so an address search
// can still find a record whose type is currently filtered out), which also
// reveals every OTHER hidden-by-filter record at the same time. This
// re-applies the entity-type filter to what actually renders, while still
// letting the specific searched-for match through even if its type is
// filtered — `preserveMatch` covers that case.
const FILTERABLE_ENTITY_TYPES = new Set([
  'lead',
  'opportunity',
  'draftlead',
  'pendinglead',
])

// Shared by filterLocationsByEntityTypes/filterLocationsByKnockStatus/
// filterEntityPinsForKnockStatusLocationsOnly below: each re-filters the merged (backend
// returns everything during a search) location set back down to what the real active filter
// would show, while still letting the specific searched-for match through even if the filter
// would otherwise have hidden it. `matchWasFiltered` is the filter-specific "would this filter
// have excluded preserveMatch" condition — evaluated by the caller so this stays generic.
function preserveSearchMatch<T extends { pinId: string }>(
  filtered: T[],
  preserveMatch: T | null | undefined,
  matchWasFiltered: boolean,
): T[] {
  if (
    preserveMatch &&
    matchWasFiltered &&
    !filtered.some((location) => location.pinId === preserveMatch.pinId)
  ) {
    return [...filtered, preserveMatch]
  }

  return filtered
}

// SH-2100: a member of a multi-record pin survives the entity-type filter when it is a PestPac
// member (never filtered — SH-586) or a non-filterable / allowed entity type. Mirrors the
// atomic-pin rule in filterLocationsByEntityTypes below, applied one level down at the member.
function memberEntityTypeAllowed(
  member: MultiTenantLocation,
  allowedTypes: Set<string>,
): boolean {
  if (isPestPacMember(member)) return true
  const type = member.entityType?.toLowerCase()
  if (!type || !FILTERABLE_ENTITY_TYPES.has(type)) return true
  return allowedTypes.has(type)
}

// SH-2100: promote a single surviving Sales Center member back to a standalone pin — parallel to
// pestPacMemberAsSinglePin — so a Lead+Opportunity multi-record pin whose Lead is filtered out
// during a search renders as the single Opportunity pin it is under the active filters, exactly as
// it does in the non-search view, instead of leaking back onto the map as a multi-record pin.
function salesCenterMemberAsSinglePin(
  member: MultiTenantLocation,
  group: MapPinDTO,
): MapPinDTO {
  return {
    ...group,
    entityId: member.entityId ?? 0,
    entityType: member.entityType,
    serviceLocationId: member.serviceLocationId,
    contactLocationId: member.contactLocationId,
    count: 1,
    multitenant: false,
    entities: [],
    latitude: member.latitude ?? group.latitude,
    longitude: member.longitude ?? group.longitude,
    knockStatusHistoryId: member.knockStatusHistoryId,
    knockStatusLocationId: member.knockStatusLocationId,
    knockStatusSettingName: member.knockStatusSettingName ?? undefined,
    knockStatusSettingColor: member.knockStatusSettingColor,
    knockStatusSettingIcon: member.knockStatusSettingIcon,
    knockStatusReason: member.knockStatusReason ?? undefined,
    knockStatusCreatedTime: member.knockStatusCreatedTime,
    knockStatusCreatedByName: member.knockStatusCreatedByName,
    knockStatusCount: member.knockStatusCount,
    pestPacLocationId: null,
    pestPacDetails: null,
  }
}

// SH-2100: re-apply the entity-type filter to the MEMBERS of a multi-record pin. During an
// overrideFilters search the backend re-clusters every colocated address in the viewport into a
// full Mixed pin (not just the searched one), so a Mixed pin fell straight through the atomic
// entity-type check and re-revealed a filtered-out member at a non-searched address. Returns [] when
// nothing survives, the single surviving pin when one member remains, or a rebuilt cluster
// (count/entityType recomputed the same way buildMixedPin does) when several remain.
function filterMultiRecordPinByEntityTypes(
  pin: MapPinDTO,
  allowedTypes: Set<string>,
): MapPinDTO[] {
  const members = pin.entities ?? []
  const remaining = members.filter((member) =>
    memberEntityTypeAllowed(member, allowedTypes),
  )

  if (remaining.length === members.length) return [pin]
  if (remaining.length === 0) return []
  if (remaining.length === 1) {
    const member = remaining[0]
    return [
      isPestPacMember(member)
        ? pestPacMemberAsSinglePin(member, pin)
        : salesCenterMemberAsSinglePin(member, pin),
    ]
  }

  const distinctTypes = new Set(
    remaining.map((member) => member.entityType?.toLowerCase()),
  )
  return [
    {
      ...pin,
      entityType: distinctTypes.size === 1 ? remaining[0].entityType : 'Mixed',
      entityId: remaining[0].entityId ?? pin.entityId,
      count: remaining.length,
      entities: remaining,
    },
  ]
}

export function filterLocationsByEntityTypes(
  locations: MapPinDTO[],
  entityTypes: string,
  preserveMatch?: MapPinDTO | null,
): MapPinDTO[] {
  const allowedTypes = new Set(
    entityTypes
      .split('_')
      .filter(Boolean)
      .map((type) => type.toLowerCase()),
  )

  const filtered = locations.flatMap((location) => {
    if (location.multitenant && (location.entities?.length ?? 0) > 0) {
      // Keep the searched-for multi-record pin whole; decompose every other one so a
      // non-searched address can't leak a filtered-out member back onto the map.
      if (preserveMatch && location.pinId === preserveMatch.pinId) {
        return [location]
      }
      return filterMultiRecordPinByEntityTypes(location, allowedTypes)
    }

    const type = location.entityType?.toLowerCase()
    if (!type || !FILTERABLE_ENTITY_TYPES.has(type)) return [location]
    return allowedTypes.has(type) ? [location] : []
  })

  return preserveSearchMatch(
    filtered,
    preserveMatch,
    !!(
      preserveMatch &&
      FILTERABLE_ENTITY_TYPES.has(preserveMatch.entityType?.toLowerCase() ?? '')
    ),
  )
}

// SH-2100: parallel to filterLocationsByEntityTypes above — re-applies the knock-status filter to
// what actually renders after overrideFilters revealed every hidden-by-filter KS location. A pin is
// filtered out only when it HAS a knock-status name that isn't in the allowed list; pins with no
// knock status at all are left untouched (a KS filter never hid them in the first place).
export function filterLocationsByKnockStatus(
  locations: MapPinDTO[],
  allowedKnockStatusNames: string[],
  preserveMatch?: MapPinDTO | null,
): MapPinDTO[] {
  const allowedNames = new Set(allowedKnockStatusNames)

  const filtered = locations.filter((location) => {
    const name = location.knockStatusSettingName
    if (!name) return true
    return allowedNames.has(name)
  })

  return preserveSearchMatch(
    filtered,
    preserveMatch,
    !!(
      preserveMatch &&
      preserveMatch.knockStatusSettingName &&
      !allowedNames.has(preserveMatch.knockStatusSettingName)
    ),
  )
}

// SH-2100: parallel to filterLocationsByEntityTypes/filterLocationsByKnockStatus above — re-applies
// the "Knock Status Locations Only" mode's entity-pin exclusion client-side after overrideFilters
// revealed every hidden-by-filter record, so a searched-for lead/opportunity/pending-lead can be
// preserved instead of vanishing outright (the thunk itself skips this strip when overrideFilters
// is true — see getContactLocationsInArea — precisely so this data survives long enough to reach here).
export function filterEntityPinsForKnockStatusLocationsOnly(
  locations: MapPinDTO[],
  preserveMatch?: MapPinDTO | null,
): MapPinDTO[] {
  // A co-located Sales Center + PestPac pin merged by buildMixedPin is a case this must not
  // drop: it keeps the Sales Center anchor's real (positive) entityId, but its entities[]
  // carries the merged PestPac member (stamped with pestPacLocationId by pestPacPinAsMember /
  // the backend's BuildPestPacClusterPin), so it still represents a PestPac location
  // underneath and must stay visible for the same SH-586 reason a bare PestPac pin does.
  const hasPestPacComponent = (location: MapPinDTO) =>
    location.entities?.some(isPestPacMember) ?? false

  // isEntityIdEmpty covers the plain PestPac-pin case: EntityId is a nullable long on the
  // backend and is never set for a PestPac-sourced pin (BuildPestPacClusterPin /
  // MapPestPacItemToEntityLocationDTO), so it arrives here as entityId: null, not 0.
  const filtered = locations.filter(
    (location) => isEntityIdEmpty(location) || hasPestPacComponent(location),
  )

  return preserveSearchMatch(
    filtered,
    preserveMatch,
    !!(preserveMatch && preserveMatch.entityId > 0),
  )
}

// SH-407: a PestPac pin already carries the knock-status badge (stamped server-side), so a
// standalone knock-status-only pin (entityId 0/null) at the same rooftop is a visual duplicate.
// Drop it here. This also covers the FIRST map load, before the server-side "PestPacLocation" tie
// flip has persisted (the SalesCenter search and the PestPac fetch run in parallel per tile, so the
// SalesCenter side can still return the not-yet-flipped standalone pin on that first load).
export function excludeKnockStatusPinsCoveredByPestPac(
  salesCenterLocations: MapPinDTO[],
  pestPacLocations: MapPinDTO[],
): MapPinDTO[] {
  if (pestPacLocations.length === 0) return salesCenterLocations

  const pestPacCells = new Map<string, { lat: number; lng: number }[]>()
  const indexCoordinate = (lat?: number | null, lng?: number | null) => {
    if (lat == null || lng == null) return
    const key = cellKey(lat, lng)
    const bucket = pestPacCells.get(key)
    if (bucket) bucket.push({ lat, lng })
    else pestPacCells.set(key, [{ lat, lng }])
  }

  for (const pin of pestPacLocations) {
    indexCoordinate(pin.latitude, pin.longitude)
    for (const member of pin.entities ?? []) {
      indexCoordinate(member.latitude, member.longitude)
    }
  }

  const coveredByPestPac = (loc: MapPinDTO) =>
    neighbouringCellKeys(loc.latitude, loc.longitude)
      .flatMap((key) => pestPacCells.get(key) ?? [])
      .some((coord) =>
        areColocatedCoordinates(
          coord.lat,
          coord.lng,
          loc.latitude,
          loc.longitude,
        ),
      )

  return salesCenterLocations.filter(
    (loc) => !(isKnockStatusOnlyPin(loc) && coveredByPestPac(loc)),
  )
}

export function mergeColocatedSalesCenterAndPestPacPins(
  salesCenterLocations: MapPinDTO[],
  pestPacLocations: MapPinDTO[],
): MapPinDTO[] {
  if (salesCenterLocations.length === 0 || pestPacLocations.length === 0) {
    return [...salesCenterLocations, ...pestPacLocations]
  }

  const pestPacByCell = new Map<string, Set<MapPinDTO>>()
  for (const pin of pestPacLocations) {
    for (const coordinate of pinCoordinates(pin)) {
      const key = cellKey(coordinate.latitude, coordinate.longitude)
      const bucket = pestPacByCell.get(key)
      if (bucket) bucket.add(pin)
      else pestPacByCell.set(key, new Set([pin]))
    }
  }

  const merged: MapPinDTO[] = []
  const consumedPestPacPins = new Set<MapPinDTO>()

  for (const salesCenterPin of salesCenterLocations) {
    const salesCenterCoordinates = pinCoordinates(salesCenterPin)
    const candidates = new Set(
      salesCenterCoordinates.flatMap((coordinate) =>
        neighbouringCellKeys(coordinate.latitude, coordinate.longitude).flatMap(
          (key) => [...(pestPacByCell.get(key) ?? [])],
        ),
      ),
    )
    const partners = isKnockStatusOnlyPin(salesCenterPin)
      ? []
      : pestPacLocations.filter(
          (pin) =>
            candidates.has(pin) &&
            !consumedPestPacPins.has(pin) &&
            haveColocatedCoordinates(
              salesCenterCoordinates,
              pinCoordinates(pin),
            ),
        )

    if (partners.length === 0) {
      merged.push(salesCenterPin)
      continue
    }

    partners.forEach((pin) => consumedPestPacPins.add(pin))
    merged.push(buildMixedPin(salesCenterPin, partners))
  }

  for (const pin of pestPacLocations) {
    if (!consumedPestPacPins.has(pin)) merged.push(pin)
  }

  return merged
}

// Shared by isKnockStatusOnlyPin below and filterEntityPinsForKnockStatusLocationsOnly above —
// entityId is a nullable long on the backend, so "no real entity behind this pin" means either.
function isEntityIdEmpty(pin: MapPinDTO): boolean {
  return pin.entityId === 0 || pin.entityId == null
}

function isKnockStatusOnlyPin(pin: MapPinDTO): boolean {
  return isEntityIdEmpty(pin) && pin.entityType?.toLowerCase() === 'knockstatus'
}

type PinCoordinate = { latitude: number; longitude: number }

function pinCoordinates(pin: MapPinDTO): PinCoordinate[] {
  return [
    { latitude: pin.latitude, longitude: pin.longitude },
    ...(pin.entities ?? []).map((member) => ({
      latitude: member.latitude,
      longitude: member.longitude,
    })),
  ].filter(
    (coordinate): coordinate is PinCoordinate =>
      coordinate.latitude != null && coordinate.longitude != null,
  )
}

function haveColocatedCoordinates(
  a: PinCoordinate[],
  b: PinCoordinate[],
): boolean {
  return a.some((first) =>
    b.some((second) =>
      areColocatedCoordinates(
        first.latitude,
        first.longitude,
        second.latitude,
        second.longitude,
      ),
    ),
  )
}

function pinMembers(pin: MapPinDTO): MultiTenantLocation[] {
  if (pin.multitenant && pin.entities?.length) return pin.entities

  const pestPacLocationId =
    pin.pestPacLocationId ?? pin.pestPacDetails?.locationId
  const hasBackingRecord = !!pin.entityId || !!pin.contactLocationId

  if (pestPacLocationId && !hasBackingRecord) {
    return [pestPacPinAsMember(pin, pestPacLocationId)]
  }

  // SH-2100: carry the pin's knock status onto its member so it survives if a co-located member
  // is later filtered out and this one is promoted back to a standalone pin.
  const member: MultiTenantLocation = {
    entityType: pin.entityType,
    entityId: pin.entityId,
    serviceLocationId: pin.serviceLocationId,
    contactLocationId: pin.contactLocationId,
  }
  for (const field of KNOCK_STATUS_FIELDS) {
    Object.assign(member, { [field]: pin[field] })
  }

  return [member]
}

function memberIdentity(member: MultiTenantLocation): string {
  if (isPestPacMember(member)) return `pestpac|${member.pestPacLocationId}`

  return `${member.entityType?.toLowerCase()}|${member.entityId}|${member.serviceLocationId ?? ''}`
}

function dedupeMembers(members: MultiTenantLocation[]): MultiTenantLocation[] {
  const seen = new Set<string>()

  return members.filter((member) => {
    const identity = memberIdentity(member)
    if (seen.has(identity)) return false

    seen.add(identity)
    return true
  })
}

function buildMixedPin(
  salesCenterPin: MapPinDTO,
  pestPacPins: MapPinDTO[],
): MapPinDTO {
  const entities = dedupeMembers([
    ...pinMembers(salesCenterPin),
    ...pestPacPins.flatMap(pinMembers),
  ])
  const distinctTypes = new Set(
    entities.map((member) => member.entityType?.toLowerCase()),
  )

  // The anchor's pinId is kept rather than re-derived: it is the id the server sends for this record
  // on every refresh, so the open bubble still re-syncs and an unmerged appearance of the same record
  // (a PestPac fetch that failed) cannot slip past the dedupe under a second id (SH-1327).
  return {
    ...salesCenterPin,
    entityType: distinctTypes.size === 1 ? salesCenterPin.entityType : 'Mixed',
    count: entities.length,
    multitenant: true,
    entities,
    serviceLocationId: undefined,
    contactLocationId: undefined,
    pestPacLocationId: null,
    pestPacDetails: null,
  }
}

export function isPestPacPin(pin: MapPinDTO): boolean {
  return pin.entityType?.toLowerCase() === MapPinTypeVariant.PestPac
}

export async function fetchPestPacLocationDetail(
  locationId: string,
): Promise<any> {
  const url = `${ServiceConfig.baseAPIUrl}/ContactLocations/pestpac/${encodeURIComponent(
    locationId,
  )}/details`

  const response = await get(url)
  if (!response.ok) {
    const error = (await response.json()) as APIError
    throw new Error(error.Message || `Failed to fetch PestPac location detail`)
  }

  return await response.json()
}
